/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useMemo } from 'react';
import { useBannerCookieConsent } from '@jira/platform__banners-cookie-consent/src/useBannerCookieConsent.tsx';
import { functionUnionWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import { useBannerIPBlocklisting } from '@jira/platform__ip-block-listing-banner/src/controllers/index.tsx';
import { useIsChromeless } from '@jira/platform__controllers-layout/src/controllers/layout-controller/consumers/chromeless/index.tsx';
import { useBannerAnnouncement } from '@jira/platform__banners-announcement/src/useBannerAnnouncement.tsx';
import { useCohort3CustomersBanner } from '@jira/platform__banners-eoc-cohort3-customers-banner/src/useCohort3CustomersBanner.tsx';
import { useBannerEocTransition } from '@jira/platform__banners-eoc-transition/src/useBannerEocTransition.tsx';
import { useBannerMailExpiry } from '@jira/platform__banners-mail-expiry-reminder/src/useBannerMailExpiry.tsx';
import { useBannerReturnToMobile } from '@jira/platform__return-to-mobile-banner/src/controllers/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';

type ReturnType = {
	name: string | null;
	height: number;
	isEnabled: boolean;
};

const noBanner: ReturnType = {
	name: null,
	height: 0,
	isEnabled: false,
};

type Props = {
	ignoreChromeless?: boolean;
};

const useCohort3CustomersBannerConditional = functionUnionWithCondition(
	() => fg('eoc_consolidation_cohort_3_banner'),
	useCohort3CustomersBanner,
	() => undefined,
);

export const useBanner = ({ ignoreChromeless = false }: Props = {}): ReturnType => {
	const [isChromeless] = useIsChromeless();
	const announcement = useBannerAnnouncement();

	const transition = useBannerEocTransition();
	const returnToMobile = useBannerReturnToMobile();
	const IPBlocklisting = useBannerIPBlocklisting();

	const cohort3CustomersBanner = useCohort3CustomersBannerConditional();

	const cookieConsent = useBannerCookieConsent();

	// we have added this check to return banner data for assets routes as all the routes in assets are chromeless
	// but we want to show the announcement banner in assets routes
	const isChromelessCheck = ignoreChromeless ? false : isChromeless;
	const mailExpiry = useBannerMailExpiry();

	return useMemo(() => {
		// cookie consent banner has the highest priority
		if (cookieConsent?.isEnabled) return cookieConsent;
		if (isChromelessCheck) return noBanner;
		if (IPBlocklisting.isEnabled) return IPBlocklisting;
		if (returnToMobile.isEnabled) return returnToMobile;
		if (announcement.isEnabled) return announcement;
		if (transition?.isEnabled) return transition;
		if (mailExpiry?.isEnabled) return mailExpiry;
		if (cohort3CustomersBanner?.isEnabled) return cohort3CustomersBanner;
		return noBanner;
	}, [
		cookieConsent,
		IPBlocklisting,
		returnToMobile,
		announcement,
		transition,
		mailExpiry,
		cohort3CustomersBanner,
		isChromelessCheck,
	]);
};
